import type { ConsoleScript } from '../console/types';
import { Pause } from '../console/commands/Pause';
import { ForceAcknowledge } from '../console/commands/ForceAcknowledge';
import { SpawnBeings } from '../console/commands/SpawnBeings';
import { RestartGame } from '../console/commands/RestartGame';
import { PubSub } from '../PubSub';
import type { GameEvents } from '../events';

export class ConsoleController {
    private cancelFlag = { cancelled: false };
    private current: Promise<void> = Promise.resolve();

    constructor(private pubsub: PubSub<GameEvents>) {}

    /**
     * Plays a script, cancelling any currently running script first. The returned
     * promise resolves once the script has finished playing and been dismissed —
     * either by the player pressing Enter or by a FORCE_ACKNOWLEDGE ending.
     *
     * Cancelling a script via a subsequent `play()` does not resolve its promise
     * immediately: a script left waiting for its dismiss keypress only resolves on
     * the next keydown (its listener stays registered until then). Callers that
     * chain off a script should use `whenIdle()`, which always tracks the most
     * recent `play()`.
     */
    play(script: ConsoleScript): Promise<void> {
        this.cancelFlag.cancelled = true;
        this.pubsub.publish('console:stop-typing');

        const flag = { cancelled: false };
        this.cancelFlag = flag;

        this.current = this.runScript(script, flag);
        return this.current;
    }

    /**
     * Resolves once the console is idle — i.e. the currently playing script (if any)
     * has been dismissed. Used to chain one script after another finishes.
     */
    whenIdle(): Promise<void> {
        return this.current;
    }

    /** Hides the console immediately, cancelling any running script. */
    dismiss(): void {
        this.cancelFlag.cancelled = true;
        this.pubsub.publish('console:stop-typing');
        this.pubsub.publish('console:hide-input');
        this.pubsub.publish('console:hide');
    }

    /**
     * Executes each token in `script` in order: strings are typed character-by-character,
     * commands are dispatched to their handler classes. After the last token, the console
     * is dismissed automatically (immediately if the script ended with a FORCE_ACKNOWLEDGE,
     * or on the next Enter key press otherwise).
     */
    private async runScript(script: ConsoleScript, flag: { cancelled: boolean }): Promise<void> {
        await this.pubsub.publish('console:show');
        await this.pubsub.publish('console:clear');

        let pendingInline = false;
        let endsWithForceAcknowledge = false;

        for (const token of script) {
            if (flag.cancelled) return;

            if (typeof token === 'string') {
                if (pendingInline) {
                    await this.pubsub.publish('console:type-inline', token);
                    pendingInline = false;
                } else {
                    await this.pubsub.publish('console:type-line', token);
                }
                endsWithForceAcknowledge = false;
            } else {
                switch (token.command) {
                    case 'PAUSE_INLINE':
                        await new Pause(token.duration ?? 1000).execute(this.pubsub, flag);
                        pendingInline = true;
                        endsWithForceAcknowledge = false;
                        break;
                    case 'FORCE_ACKNOWLEDGE':
                        await new ForceAcknowledge(token.prompt, token.expected).execute(this.pubsub, flag);
                        endsWithForceAcknowledge = true;
                        break;
                    case 'SPAWN_BEINGS':
                        await new SpawnBeings(token.count).execute(this.pubsub, flag);
                        break;
                    case 'RESTART_GAME':
                        await new RestartGame().execute(this.pubsub, flag);
                        break;
                }
            }
        }

        if (flag.cancelled) return;

        if (endsWithForceAcknowledge) {
            this.pubsub.publish('console:hide');
        } else {
            await this.waitForEnterToDismiss(flag);
        }
    }

    /**
     * Resolves when the player presses Enter (hiding the console) or when the script
     * is cancelled. The listener removes itself in either case.
     */
    private waitForEnterToDismiss(flag: { cancelled: boolean }): Promise<void> {
        return new Promise<void>((resolve) => {
            const handler = (e: KeyboardEvent) => {
                if (flag.cancelled) {
                    window.removeEventListener('keydown', handler);
                    resolve();
                    return;
                }
                if (e.key !== 'Enter') {
                    return;
                }
                window.removeEventListener('keydown', handler);
                this.pubsub.publish('console:hide');
                resolve();
            };
            window.addEventListener('keydown', handler);
        });
    }
}
